import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import { inputStyles } from 'assets/components/inputs/Input';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { compact, flatten, get, intersectionBy } from 'lodash';
import Permission from 'models/core/permission/Model.Permission';
import Role from 'models/core/role/Model.Role';
import User from 'models/core/user/Model.User';
import userSaveApi from 'models/core/user/save/Api.User.Save';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useMemo } from 'react';
import UserEditAdditionalPermissions from './edit/User.Edit.AdditionalPermissions';
import UserEditRoles from './edit/User.Edit.Roles';

export default function UserEdit(
  props: Template.ModalForm.MinimalImport<
    Model.IUser,
    Api.Core.User.Save.IRequest,
    Api.Res<Api.Core.User.Save.IResponse>
  >
) {
  const { permissions: currentUserPermissions, user, isDealer, isDistributor } = useAuthStore();
  const { lang } = getLocales();
  const save = useApi({ action: userSaveApi, callback: props.onSave, wait: true });

  const permissionsApi = useApi({ action: Permission.list });
  const rolesApi = useApi({ action: Role.list });

  const { payload: dealersPayload } = useApi({
    action: Dealer.list,
    body: {
      id: compact(user?.dealers?.map((d) => d.id)),
    },
  });
  const { payload: distributorsPayload } = useApi(
    {
      action: Distributor.list,
      body: {
        id: compact([user?.distributor?.id ?? user?.distributorId]),
      },
      wait: !user,
    },
    [user]
  );

  const permissionList = useMemo(
    () =>
      compact(permissionsApi.payload?.data).filter((it) =>
        isDistributor || isDealer ? get(currentUserPermissions, it.key) : true
      ),
    [permissionsApi.payload?.data]
  );

  const roleList = useMemo(() => {
    const filteredRoles = compact(rolesApi.payload?.data).filter((it) =>
      isDistributor || isDealer ? it.permissions.every((p) => get(currentUserPermissions, p.key)) : true
    );
    for (const role of filteredRoles) {
      role.permissions = role.permissions.map((rp) => permissionList.find((p) => p.id === rp.id) ?? rp);
    }
    return filteredRoles;
  }, [rolesApi.payload?.data, permissionList]);

  return (
    <>
      <ModalForm<Api.Core.User.Save.IRequest>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[props.data]}
        title={lang.user}
        default={props.data}
        onCancel={props.onCancel}
        onSubmit={(data) => save.execute(data)}
        validation={(data, errors) => {
          if (!data.firstName) errors.firstName = lang.mustNotBeEmpty;
          if (!data.lastName) errors.lastName = lang.mustNotBeEmpty;
          if (!data.email) errors.email = lang.mustNotBeEmpty;
          if (!data.languageIsoCode) errors.languageIsoCode = lang.mustNotBeEmpty;
          if (!data?.id && !User.checkPasswordValidity(data.password)) errors.password = lang.passwordValidityMessage;
          if (!data?.roles?.length) errors.roles = { _objectError: lang.mustSelectAtleastOneOption };
          if ((isDistributor || isDealer) && !data.distributorId) errors.distributorId = lang.mustNotBeEmpty;
          if ((isDistributor || isDealer) && !data.dealers?.length)
            errors.dealers = { _objectError: lang.mustSelectAtleastOneOption };
        }}
        beforeUpdate={(data, updates) => {
          if (updates.roles) {
            const currentPermissions = flatten(
              data?.roles.map((it) => rolesApi.payload?.data?.find((r) => r.id == it.id)?.permissions)
            ).filter((it) => it);
            data.additionalPermissions = data.additionalPermissions?.filter(
              (it) => !currentPermissions?.find((p) => p.id == it.permission?.id)
            );
          }
          return data;
        }}
        tabs={[
          { id: 'info', icon: 'fas-user', text: lang.info },
          {
            id: 'role',
            icon: 'fas-user-shield',
            text: lang.roles,
            errors: ({ form }) => form.errors?.roles?._objectError,
          },
          { id: 'additionalPermissions', icon: 'fas-shield-alt', text: lang.additionalPermissions },
        ]}
        render={({ form, activeTab }) => {
          const selectableDistributors =
            distributorsPayload?.data?.filter((it) => (user?.distributorId ? user?.distributorId === it.id : true)) ??
            [];
          const selectableDealers =
            dealersPayload?.data?.filter((d) => d.representativeId === form.data.distributorId) || [];

          return (
            <>
              {activeTab?.id == 'info' && (
                <FormWrapper>
                  <InputWrapper>
                    <TextInput
                      name="firstName"
                      label={lang.firstName}
                      value={form.data.firstName}
                      error={form.errors.firstName}
                      onChange={form.update}
                      icon={<Icon class="if-quill-pen" />}
                    />
                    <TextInput
                      name="lastName"
                      label={lang.lastName}
                      value={form.data.lastName}
                      error={form.errors.lastName}
                      onChange={form.update}
                      icon={<Icon class="if-quill-pen" />}
                    />
                  </InputWrapper>
                  <TextInput
                    name="inpute"
                    label={lang.email}
                    value={form.data.email}
                    error={form.errors.email}
                    onChange={({ inpute: email }) => form.update({ email })}
                    icon={<Icon class="if-envelope" />}
                  />
                  <TextInput
                    name="internalSupportDeskUid"
                    label={lang.internalSupportDeskUid}
                    value={form.data.internalSupportDeskUid}
                    error={form.errors.internalSupportDeskUid}
                    onChange={form.update}
                    icon={<Icon class="fas-headset" />}
                  />
                  {!props.data?.id && (
                    <>
                      <TextInput
                        name="inputp"
                        label={lang.password}
                        value={form.data.password}
                        error={form.errors.password}
                        onChange={({ inputp: password }) => form.update({ password })}
                        type="password"
                        autoComplete="new-password"
                        icon={<Icon class="if-key" />}
                      />
                      <CheckWrapper>
                        <CheckInput
                          name="sendResetPasswordEmail"
                          label={lang.sendResetPasswordEmail}
                          class={inputStyles.check.switch}
                          value={form.data.sendResetPasswordEmail}
                          onChange={form.update}
                          htmlElementProps={{
                            title: form.data.sendResetPasswordEmail ? lang.disable : lang.enable,
                          }}
                        />
                      </CheckWrapper>
                      &nbsp;
                    </>
                  )}
                  <CheckWrapper>
                    <CheckInput
                      name="isDisabled"
                      label={lang.isDisabled}
                      class={inputStyles.check.switch}
                      value={form.data.isDisabled}
                      onChange={form.update}
                      htmlElementProps={{
                        title: form.data.isDisabled ? lang.disable : lang.enable,
                      }}
                    />
                  </CheckWrapper>
                  &nbsp;
                  <SelectInput
                    name="languageIsoCode"
                    label={lang.language}
                    data={Object.values(Language).map((id) => ({ id, title: lang[id] }))}
                    value={form.data.languageIsoCode}
                    error={form.errors.languageIsoCode}
                    onChange={form.update}
                    icon={<Icon class="fas-globe" />}
                  />
                  <SelectInput
                    name="distributorId"
                    disabled={isDealer}
                    label={lang.distributor}
                    data={selectableDistributors.map((it) => it.displayInfo)}
                    value={form.data.distributorId}
                    error={form.errors.distributorId}
                    onChange={({ distributorId }) => {
                      const distributor = selectableDistributors?.find((d) => d.id === distributorId);
                      form.update({
                        distributorId: distributor?.id,
                        distributor,
                        dealers: intersectionBy(distributor?.dealers || [], form.data.dealers || [], (d) => d.id),
                      });
                    }}
                    icon={<Icon class="fas-globe" />}
                  />
                  <SelectInput
                    name="dealers"
                    label={lang.dealers}
                    data={selectableDealers?.map((it) => it.displayInfo)}
                    value={form.data.dealers?.map((it) => it.id)}
                    error={form.errors.dealers?._objectError}
                    onChange={(selectData) =>
                      form.update({
                        dealers: selectData.dealers
                          .map((it) => selectableDealers?.find((d) => d.id === it))
                          ?.filter((it) => (it ? true : false)),
                      })
                    }
                    isMultiselect
                    icon={<Icon class="fas-globe" />}
                  />
                </FormWrapper>
              )}
              {activeTab?.id === 'role' && (
                <UserEditRoles
                  roles={roleList}
                  permissions={permissionList}
                  isLoading={permissionsApi.isExecuting || rolesApi.isExecuting}
                />
              )}

              {activeTab?.id == 'additionalPermissions' && (
                <UserEditAdditionalPermissions
                  roles={roleList}
                  permissions={permissionList}
                  isLoading={permissionsApi.isExecuting || rolesApi.isExecuting}
                />
              )}
            </>
          );
        }}
      />
    </>
  );
}
